import CompFinalConverter from '@/converters/3.6/CompFinalConverter';
import IComps from '@/models/comp/IComps';
import IUserSettings from '@/models/IUserSettings';
import HostExchangeService from '@/services/3.6/HostExchangeService';
import { store, Getters, Mutations, mlsStore, mlsStoreV36, salesHistoryStore, userSettingsStore } from '@/store';
import CompsManager from './CompsManager';
import _ from 'lodash';
import CompUtils from '@/models/comp/CompUtils';
import CompFinalDataUtils from '@/models/comp-final/CompFinalDataUtils';
import { SourceDataInitials } from '@/constants/SourceDataInitials';
import IComp from '@/models/Comp';
import LogUsageManager from '../LogUsageManager';
import ICompData from '@/models/comp/ICompData';
import CompDataUtils from '@/models/comp/CompDataUtils';
import FactSheetManager from './FactSheetManager';
import SalesHistoryManager from './SalesHistoryManager';
import IExchangeReportFields from '@/models/exchange/IExchangeReportFields';
import IExchangeReportField from '@/models/exchange/IExchangeReportField';
import { FieldNames } from '@/constants/FieldNames';
import OriginalValuesManager from './OriginalValuesManager';
import QSUtils from '@/utils/QSUtils';
import ICompFinalData from '@/models/comp-final/ICompFinalData';
import { nextTick } from 'vue';
// import TimingsUtil from '@/utils/TimingsUtil';

const waitTimeMillis = 200;
const maxAvailableAttempts = 20;

export default {
    // True while sendAll() silently cycles the active comp, so the UI can suppress prompts.
    isSendingAll: false,

    async waitOnAvailability(): Promise<boolean> {
        const hostExchangeService = HostExchangeService.getInstance();

        let attempts = 0;
        let available = false;
        do {
            if (attempts > 0) {
                await QSUtils.sleep(waitTimeMillis);
            }

            available = await hostExchangeService.isAvailable();
            attempts++;
        } while (!available && attempts < maxAvailableAttempts);

        if (available) {
            if (attempts > 1) {
                console.log(`HostExchangeManager.waitOnAvailability(), Available after [${attempts}] attempts, wait time[${waitTimeMillis}(ms)].`);
            } else {
                console.log(`HostExchangeManager.waitOnAvailability(), Available on first attempt.`);
            }
        } else {
            console.log(`HostExchangeManager.waitOnAvailability(), Not available after [${attempts}] attempts, wait time[${waitTimeMillis}(ms)].`);
        }

        return available;
    },

    /**
     * Sends all data required by our host service. Pulls various data from the store. Manages
     * updating, converting and sending.
     */
    async sendAll() {
        if(QSUtils.debugger()) {
            console.log("sendAll");
        }

        // Flush pending reactive updates before taking the export snapshot.
        await nextTick();
        await nextTick();

        // Cycle through every non-subject comp so the DataGrid watcher fires for each one.
        // That triggers buildGridPayload() → applyTransferToFormForRepeatingSections() which
        // populates compound/repeatable rows. We use SetSilentActiveComp so state.activeComp
        // changes (triggering the watcher) without touching comp.isActive — the tab highlight
        // therefore never moves in the UI.
        const originalActiveCompIdx = store.getters[Getters.CompAddressesV36.Comps].findIndex((c: any) => c.isActive);
        const allCompsForCycle: IComps = store.getters[Getters.CompAddressesV36.Comps];
        this.isSendingAll = true;
        try {
            for (let i = 0; i < allCompsForCycle.length; i++) {
                const cycleComp = allCompsForCycle[i];
                if (cycleComp.isSubject || cycleComp.isActive) {
                    continue;
                }
                // Silently switch activeComp — DataGrid watcher fires, tab highlight stays put.
                store.commit(Mutations.CompAddressesV36.SetSilentActiveComp, i);
                // Wait for Vue reactivity + buildGrid() + applyTransferToFormForRepeatingSections() to complete
                await nextTick();
                await nextTick();
                await QSUtils.sleep(800);
            }
            // Restore the originally active comp (also silently — highlight never moved)
            if (originalActiveCompIdx !== -1) {
                store.commit(Mutations.CompAddressesV36.SetSilentActiveComp, originalActiveCompIdx);
                await nextTick();
                await nextTick();
                await QSUtils.sleep(400);
            }
        } finally {
            this.isSendingAll = false;
        }

        const comps: IComps = store.getters[Getters.CompAddressesV36.Comps];
        const userSettings: IUserSettings = userSettingsStore.UserSettings!;
        if(QSUtils.debugger()) {
            console.log("comps result: ", comps);
            console.log("userSettings result: ", userSettings);
        }

        const hostExchangeService = HostExchangeService.getInstance();

        //const timings = new TimingsUtil();
        // timings.addMark('SendAll_Start');

        // Comps data - read fresh from store immediately before conversion
        // Converter will read live store data for each comp and apply revert logic
        const liveComps: IComps = store.getters[Getters.CompAddressesV36.Comps];
        const compsToExportFiltered = filterCompsToExport(liveComps, userSettings);
        if(QSUtils.debugger()) {
            console.log("compsToExport result: ", compsToExportFiltered);
        }

        let compFinalData = CompFinalConverter.convert(compsToExportFiltered, userSettings);
        compFinalData = CompFinalConverter.appendAdditionalProperties(compFinalData);
        const compFinalDataForExport = CompFinalConverter.compFinalDataStructure(_.cloneDeep(compFinalData));

        console.log("compFinalData", compFinalData);
        console.log("compFinalDataForExport", compFinalDataForExport);

        if(QSUtils.debugger()) {
            console.log("CompFinalConverter result: ", compFinalData);
        }

        // Export Prior Sales and History Analyses
        const finalCompsPromise = sendFinalCompsAndReportFieldData(compFinalDataForExport);

        if(QSUtils.debugger()) {
            console.log("sendFinalCompsAndReportFieldData completed");
        }

        // Save MLS data, CSV file, Original Values and Sales History Selections
        const fileContentPromise = sendFileContent(compFinalData);

        if(QSUtils.debugger()) {
            console.log("sendFileContent completed");
        }

        // Work File data
        const workFile = await FactSheetManager.createFromCompFinalData(compFinalDataForExport.TotalComps);
        if(QSUtils.debugger()) {
            console.log("workFile result: ", workFile);
        }

        const dataFactSheetPromise = hostExchangeService.placeDataFactSheetInWorkfile(workFile);

        await Promise.all([finalCompsPromise, fileContentPromise, dataFactSheetPromise]);
    },

    /**
     * Use the active comp and any corresponding MLS property data to create a fact sheet.
     * Send it to our host for launching in a browser.
     */
    async requestActiveCompFactSheet() {
        const activeComp: IComp = store.getters[Getters.CompAddressesV36.ActiveComp];
        if (activeComp) {
            const factSheet = await FactSheetManager.createFromComp(activeComp);

            const service = HostExchangeService.getInstance();
            service.sendDataFactSheet(factSheet, activeComp.isSubject ?? false); // No await
        }
    },

    /**
     * Notify host it's time to close the application. Log a message when saved is false.
     */
    async closeApp(saved: boolean) {
        if (!saved) {
            await LogUsageManager.closedWithoutSave();
        }

        const hostExchangeService = HostExchangeService.getInstance();
        await hostExchangeService.closeForm();
    },
};


async function sendFinalCompsAndReportFieldData(compFinalDataForExport: any): Promise<void> {
    if(QSUtils.debugger()) {
        console.log("sendFinalCompsAndReportFieldData method");
    }

    const hostExchangeService = HostExchangeService.getInstance();

    await hostExchangeService.exportFinalComps(compFinalDataForExport);
    if(QSUtils.debugger()) {
        console.log("exportFinalComps completed");
    }

    const compsForExport = Array.isArray(compFinalDataForExport)
        ? compFinalDataForExport
        : (compFinalDataForExport?.TotalComps ?? []);

    await logExported(compsForExport);
    if(QSUtils.debugger()) {
        console.log("logExported completed");
    }

    // Report Field data
    const reportFields: IExchangeReportFields = [];

    // Handle analysis of prior sales
    const priorSalesAnalyses = await SalesHistoryManager.generateAnalyses(compsForExport);
    if(QSUtils.debugger()) {
        console.log("priorSalesAnalyses result: ", priorSalesAnalyses);
    }

    if (priorSalesAnalyses.length > 0) {
        const reportField: IExchangeReportField = {
            fieldName: FieldNames.AnalysisOfPriorSale,
            value: priorSalesAnalyses,
        };
        reportFields.push(reportField);
    }

    // Handle any sales histories marked for the addenda
    const salesHistoryProperties = await SalesHistoryManager.getForAddenda(compsForExport);
    if(QSUtils.debugger()) {
        console.log("salesHistoryProperties result: ", salesHistoryProperties);
    }

    if (salesHistoryProperties.length > 0) {
        const reportField: IExchangeReportField = {
            fieldName: FieldNames.AddendaWithSalesHistories,
            value: salesHistoryProperties,
        };
        reportFields.push(reportField);
    }

    if(QSUtils.debugger()) {
        console.log("reportFields result: ", reportFields);
    }

    // There may or may not be report fields to send
    if (reportFields.length > 0) {
        await hostExchangeService.pushReportFieldDataToReport(reportFields);
    }
}

async function logExported(compFinalDataForExport: any): Promise<void> {
    if(QSUtils.debugger()) {
        console.log("logExported method");
    }

    const quickSourceDataPromise = LogUsageManager.exportedQuickSourceData(compFinalDataForExport.length - 1); // Don't count the subject property
    if(QSUtils.debugger()) {
        console.log("quickSourceDataPromise result: ", quickSourceDataPromise);
    }

    const seExists = CompFinalDataUtils.containsTransferValueFromSource36(compFinalDataForExport, SourceDataInitials.SmartExchange);
    if(QSUtils.debugger()) {
        console.log("seExists result: ", seExists);
    }
    const smartExchangeDataPromise = LogUsageManager.exportedSmartExchangeData(seExists);

    const prExists = CompFinalDataUtils.containsTransferValueFromSource36(compFinalDataForExport, SourceDataInitials.PublicRecords);
    if(QSUtils.debugger()) {
        console.log("prExists result: ", prExists);
    }
    const publicRecordsPromise = LogUsageManager.exportedPublicRecordsData(prExists);

    const mlsExists = CompFinalDataUtils.containsTransferValueFromSource36(compFinalDataForExport, SourceDataInitials.Mls);
    if(QSUtils.debugger()) {
        console.log("mlsExists result: ", mlsExists);
    }
    const transferValueFromSourcePromise = LogUsageManager.exportedMlsData(mlsExists);

    const dbExists = CompFinalDataUtils.containsTransferValueFromSource36(compFinalDataForExport, SourceDataInitials.Database);
    if(QSUtils.debugger()) {
        console.log("dbExists result: ", dbExists);
    }
    const localDatabasePromise = LogUsageManager.exportedLocalCompDBData(dbExists);

    await Promise.all([quickSourceDataPromise, smartExchangeDataPromise, publicRecordsPromise, transferValueFromSourcePromise, localDatabasePromise]);
}

async function sendFileContent(compFinalData: ICompFinalData): Promise<void> {

    if(QSUtils.debugger()) {
        console.log("sendFileContent method");
    }

    const hostExchangeService = HostExchangeService.getInstance();
    
    // MLS data
    const mlsPropertyData = mlsStore.MlsProperties;
    if(QSUtils.debugger()) {
        console.log("mlsPropertyData result: ", mlsPropertyData);
    }

    const mlsDataPromise = hostExchangeService.putMlsDataInReport(mlsPropertyData);

    // CSV file info - old pipeline writes to mlsStore, new pipeline writes to mlsStoreV36.
    const csvFileInfos = [...mlsStore.CsvFileInfos, ...mlsStoreV36.CsvFileInfos];
    if(QSUtils.debugger()) {
        console.log("csvFileInfos result: ", csvFileInfos);
    }

    const mlsCsvInfoPromise = hostExchangeService.pushMlsCsvInfoToReport(csvFileInfos);

    // Original Values
    const compAddresses = CompFinalDataUtils.getCompAddresses(compFinalData);
    if(QSUtils.debugger()) {
        console.log("compAddresses result: ", compAddresses);
    }

    const originalValuesData = OriginalValuesManager.getOriginalValuesDataByAddresses(compAddresses);
    if(QSUtils.debugger()) {
        console.log("originalValuesData result: ", originalValuesData);
    }
    
    const originalValuesPromise = hostExchangeService.putOriginalValuesInReport(originalValuesData);

    // SalesHistorySelections
    const salesHistorySelectionsArray = salesHistoryStore.SalesHistorySelections;
    if(QSUtils.debugger()) {
        console.log("salesHistorySelectionsArray result: ", salesHistorySelectionsArray);
    }

    const salesHistorySelectionsPromise = hostExchangeService.putSalesHistorySelectionsInReport(salesHistorySelectionsArray);

    await Promise.all([mlsDataPromise, mlsCsvInfoPromise, originalValuesPromise, salesHistorySelectionsPromise]);
}

function filterCompsToExport(comps: IComps, userSettings: IUserSettings): IComps {
    const compsToExport: IComps = [];
    for (const comp of comps) {
        if (userSettings.exportAllComps || CompUtils.shouldExport(comp)) {
            compsToExport.push(comp);
        }
    }
    return compsToExport;
}
